// Step 8 — Settlement possibilities (CAXeng.md §5.1) and timeshare positions (§5.2).
import { otherParty, UNIT_MONTHS, type Case, type Child, type PartyIndex, type SettlementRow, type SettlementTable, type TimesharePosition } from '@divorceos/schema';
import { computeGuideline, payorOf, signedBy, type GuidelineOutcome } from './guideline.js';
import { normalizeCase } from './normalize.js';
import { resolveReleases, TacticSolver, tacticTarget } from './proposed.js';
import { providerOf, type EngineOptions } from './provider.js';
import { TaxContext } from './taxes.js';

export function guidelineFor(c: Case, opts: EngineOptions): { ctx: TaxContext; g: GuidelineOutcome } {
  const nc = normalizeCase(c);
  const ctx = new TaxContext(nc, providerOf(opts));
  const g = computeGuideline(ctx, { releases: 0, releaseFrom: 1, now: opts.now ?? new Date() });
  return { ctx, g };
}

/** §5.1 step size for the SS column from range = net[payor] / 50. */
export function settlementStep(range: number): number {
  if (range < 25) return 10;
  if (range < 50) return 25;
  if (range < 100) return 50;
  if (range < 250) return 100;
  if (range < 500) return 250;
  if (range < 1000) return 500;
  return 1000;
}

/** Settlement Possibilities table ("only available for tactics 2 through 8"; other tactics yield CS = 0 rows). */
export function settlementTable(c: Case, releases: number | null, opts: EngineOptions = {}): SettlementTable {
  const { ctx, g } = guidelineFor(c, opts);
  const nc = ctx.nc;
  const payor = g.csPayor;
  const payee = otherParty(payor);
  const n = resolveReleases(nc, g, releases);
  const rows: SettlementRow[] = [];
  const net: [number, number] = [g.nets[0].formulaNet, g.nets[1].formulaNet];
  const range = net[payor] / 50;
  if (range < 10) return { releases: n, payor, rows };
  const step = settlementStep(range);
  const { tactic } = tacticTarget(c.proposed.tactic, c.proposed.sub);
  const solver = tactic >= 2 && tactic <= 8 ? new TacticSolver(ctx, g, n, tactic) : null;
  const tax0: [number, number] = [g.taxes[0].incomeTax, g.taxes[1].incomeTax]; // guideline taxes (saving column)
  const base = g.noSupportTaxes; // net spendable baseline (as in the Cash Flow view)
  for (let ss = step; ss < net[payor]; ss += step) {
    const ssSigned = signedBy(ss, payor);
    let cs = 0;
    let taxes = ctx.scenario({ ss: ssSigned, releases: n, releaseFrom: payee, zeroDependents: null });
    if (solver) { const r = solver.solveCs(ssSigned); cs = payor === 0 ? r.cs : -r.cs; taxes = r.taxes; }
    const total = cs + ss;
    const sav: [number, number] = [base[0].incomeTax - taxes[0].incomeTax, base[1].incomeTax - taxes[1].incomeTax];
    const ns: [number, number] = [0, 0];
    ns[payor] = net[payor] - (total - sav[payor]);
    ns[payee] = net[payee] + total + sav[payee];
    const savG = tax0[0] - taxes[0].incomeTax + tax0[1] - taxes[1].incomeTax;
    rows.push({ ss, cs, total, netSpendable: ns, combinedNetSpendable: ns[0] + ns[1], combinedSaving: savG });
  }
  return { releases: n, payor, rows };
}

/** Child record with its timeshare set to `percent` % with party 1 (i ≤ 49: CP = party 0; i ≥ 50: CP = party 1). */
function childAtPercent(ch: Child, percent: number): Child {
  const cp: PartyIndex = percent <= 49 ? 0 : 1;
  const ncp = cp === 0 ? percent / 100 : (100 - percent) / 100;
  const base = ch.timeshare.baseUnit;
  const unit = ch.timeshare.unitType;
  const units = (ncp * UNIT_MONTHS[base]) / UNIT_MONTHS[unit];
  return { ...ch, custodialParent: cp, timeshare: { units, unitType: unit, baseUnit: base } };
}

/** §5.2 support at each percentage 0..100 of the children's time with party 1 (all children, or one child). */
export function timesharePositions(c: Case, childIndex: number | null, opts: EngineOptions = {}): TimesharePosition[] {
  const provider = providerOf(opts);
  const now = opts.now ?? new Date();
  const out: TimesharePosition[] = [];
  const children = c.children.length ? c.children : [{ name: '', custodialParent: 1 as PartyIndex, timeshare: { units: 0, unitType: 'DAY' as const, baseUnit: 'YEAR' as const } }];
  for (let i = 0; i <= 100; i++) {
    const kids = children.map((ch, k) => (childIndex === null || childIndex === k ? childAtPercent(ch, i) : ch));
    const nc = normalizeCase({ ...c, children: kids });
    const ctx = new TaxContext(nc, provider);
    const g = computeGuideline(ctx, { releases: 0, releaseFrom: 1, now });
    const total = g.presumedCs + g.ss + g.addOns;
    const payor = payorOf(total);
    const sign = payor === null ? 1 : payor === 0 ? 1 : -1;
    out.push({ percent: i, payor, cs: sign * g.presumedCs, ss: sign * g.ss, addOns: sign * g.addOns });
  }
  return out;
}

